import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../api';
import { PlanOverviewResponse } from '@dreamai/types';

interface CompanyContextType {
  memoryEnabled: boolean;
  setMemoryEnabled: (enabled: boolean) => void;
  hasSubscription: boolean;
  setHasSubscription: (has: boolean) => void;
  credits: number;
  setCredits: (credits: number) => void;
  resetsAt: string | null;
  setResetsAt: (date: string | null) => void;
  hasPersonalAssistant: boolean;
  setHasPersonalAssistant: (has: boolean) => void;
  // GET /credits/plan-overview; null until loaded or when the user has no
  // company. `billingModel === 'v3'` switches the plan page, banners and the
  // chat lockout to the subscription/usage vocabulary.
  planOverview: PlanOverviewResponse | null;
  refreshPlanOverview: () => Promise<void>;
  isOpsInternalUser: boolean;
  isMaintenanceExempt: boolean;
  opsRole: 'ops_worker' | 'ops_admin' | null;
  // Full name stored on our `users` table (not Auth0's nickname, which is
  // derived from the email local-part and can look like `ben+prod`).
  userName: string | null;
  // "Help make your assistant better" (Settings > Your details). True unless the
  // user has switched it off; null until /user-details has loaded.
  analyticsConsent: boolean | null;
  setAnalyticsConsent: (enabled: boolean) => Promise<void>;
  loading: boolean;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const CompanyProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [memoryEnabled, setMemoryEnabled] = useState<boolean>(false);
  const [hasSubscription, setHasSubscription] = useState<boolean>(false);
  const [credits, setCredits] = useState<number>(0);
  const [resetsAt, setResetsAt] = useState<string | null>(null);
  const [hasPersonalAssistant, setHasPersonalAssistant] =
    useState<boolean>(false);
  const [planOverview, setPlanOverview] = useState<PlanOverviewResponse | null>(
    null,
  );
  const [isOpsInternalUser, setIsOpsInternalUser] = useState<boolean>(false);
  const [isMaintenanceExempt, setIsMaintenanceExempt] =
    useState<boolean>(false);
  const [opsRole, setOpsRole] = useState<'ops_worker' | 'ops_admin' | null>(
    null,
  );
  const [userName, setUserName] = useState<string | null>(null);
  const [analyticsConsent, setAnalyticsConsentState] = useState<boolean | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);

  const setAnalyticsConsent = useCallback(
    async (enabled: boolean) => {
      const token = await getAccessTokenSilently();
      await api.put(
        '/analytics-consent',
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAnalyticsConsentState(enabled);
    },
    [getAccessTokenSilently],
  );

  const refreshPlanOverview = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<PlanOverviewResponse>(
        '/credits/plan-overview',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPlanOverview(response.data);
    } catch (error) {
      console.warn('Could not load plan overview:', error);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  const fetchUserDetails = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const response = await api.get('/user-details', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsOpsInternalUser(response.data.isOpsInternalUser || false);
      setIsMaintenanceExempt(response.data.isMaintenanceExempt || false);
      setOpsRole(response.data.opsRole || null);
      setAnalyticsConsentState(response.data.analyticsConsent !== false);
      setUserName(
        typeof response.data.name === 'string' && response.data.name.trim()
          ? response.data.name.trim()
          : null,
      );

      // Capture browser fingerprint for Dream Browser proxy/context mirroring.
      // Fire-and-forget — failure here is non-critical.
      api
        .post(
          '/browser-fingerprint',
          {
            userAgent: navigator.userAgent,
            locale: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            languages: Array.from(navigator.languages),
            platform: navigator.platform,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .catch((err: unknown) =>
          console.warn('Failed to send browser fingerprint:', err),
        );
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  useEffect(() => {
    refreshPlanOverview();
  }, [refreshPlanOverview]);

  return (
    <CompanyContext.Provider
      value={{
        memoryEnabled,
        setMemoryEnabled,
        hasSubscription,
        setHasSubscription,
        credits,
        setCredits,
        resetsAt,
        setResetsAt,
        hasPersonalAssistant,
        setHasPersonalAssistant,
        planOverview,
        refreshPlanOverview,
        isOpsInternalUser,
        isMaintenanceExempt,
        opsRole,
        userName,
        analyticsConsent,
        setAnalyticsConsent,
        loading,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = (): CompanyContextType => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};
